import { getPlanName } from "../../utils/helpers";

const STATUS_LABELS = {
  active: "Active",
  trialing: "Trial",
  past_due: "Past due",
  paused: "Paused",
  canceled: "Canceled",
};

const MONTHS_PER_YEAR = 12;

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "");
}

function getRawSubscription(plan) {
  return plan?.payment_information?.raw_subscription || {};
}

function getRawItems(plan) {
  const items = getRawSubscription(plan)?.items;
  return Array.isArray(items) ? items : [];
}

function getBillingCycle(plan, item) {
  const cycle = firstValue(
    item?.price?.billing_cycle,
    getRawSubscription(plan)?.billing_cycle,
    plan?.payment_information?.billing_cycle
  );

  return {
    interval: cycle?.interval || "",
    frequency: Number(cycle?.frequency) || 1,
  };
}

function getBillingPriceAmount(plan, item) {
  const topLevelAmount = Number(plan?.unit_price);
  if (Number.isFinite(topLevelAmount)) {
    return topLevelAmount;
  }

  const minorAmount = Number(item?.price?.unit_price?.amount);
  if (Number.isFinite(minorAmount)) {
    return minorAmount / 100;
  }

  return null;
}

function getCurrency(plan, item) {
  return String(
    firstValue(
      plan?.currency,
      getRawSubscription(plan)?.currency_code,
      item?.price?.unit_price?.currency_code,
      "USD"
    )
  ).toUpperCase();
}

export function formatSubscriptionMoney(amount, currency = "USD") {
  if (amount === null || amount === undefined || amount === "") {
    return "Price unavailable";
  }

  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return "Price unavailable";
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  } catch (error) {
    return `${currency} ${numericAmount}`;
  }
}

export function formatSubscriptionDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

export function getBillingIntervalLabel(cycle) {
  if (!cycle?.interval) {
    return "";
  }

  const frequency = cycle.frequency > 1 ? `${cycle.frequency} ` : "";
  const interval = cycle.interval === "year" ? "year" : cycle.interval;
  return `${frequency}${interval}${cycle.frequency > 1 ? "s" : ""}`;
}

function normalizeDiscount(discount, currency) {
  if (!discount) {
    return null;
  }

  const amount = firstValue(
    discount.amount,
    discount.amounts?.[currency],
    discount.discount_amount
  );
  const code = firstValue(discount.code, discount.id, discount.description);
  const expiresAt = firstValue(
    discount.ends_at,
    discount.expires_at,
    discount.expired_at
  );

  return {
    code: code ? String(code) : "Discount",
    amount: amount !== undefined ? formatSubscriptionMoney(amount, currency) : "",
    numericAmount: Number(amount) || 0,
    expiresAt: formatSubscriptionDate(expiresAt),
  };
}

function getManagementUrl(plan, type) {
  const paymentInformation = plan?.payment_information || {};
  const rawSubscription = paymentInformation.raw_subscription || {};
  const managementUrls = rawSubscription.management_urls || {};
  const manageUrl = firstValue(
    plan?.manage_url,
    plan?.portal_url,
    paymentInformation.manage_url,
    paymentInformation.portal_url,
    managementUrls.manage,
    managementUrls.portal
  );

  if (type === "manage") {
    if (manageUrl) {
      return getCustomerPortalUrl(manageUrl);
    }

    return getCustomerPortalUrl(getManagementUrl(plan, "cancel"), true);
  }

  if (type === "update") {
    return firstValue(
      plan?.update_url,
      paymentInformation.update_url,
      managementUrls.update_payment_method,
      managementUrls.updatePaymentMethod
    ) || "";
  }

  return firstValue(
    plan?.cancel_url,
    paymentInformation.cancel_url,
    managementUrls.cancel
  ) || "";
}

export function getCustomerPortalUrl(url, isCancelUrl = false) {
  if (!url) {
    return "";
  }

  try {
    const portalUrl = new URL(url);
    const isCancelSubscriptionLink =
      portalUrl.searchParams.get("action") === "cancel_subscription";

    if (isCancelSubscriptionLink) {
      portalUrl.search = "";
      portalUrl.hash = "";
      return portalUrl.toString();
    }

    return isCancelUrl ? "" : url;
  } catch {
    return isCancelUrl ? "" : url;
  }
}

function getPaymentMethod(plan) {
  const paymentInformation = plan?.payment_information || {};
  const rawSubscription = paymentInformation.raw_subscription || {};
  const billingDetails = rawSubscription.billing_details || {};
  const paymentMethodValue = firstValue(
    paymentInformation.payment_method,
    paymentInformation.paymentMethod,
    billingDetails.payment_method,
    billingDetails.method_details,
    plan?.payment_method,
    plan?.paymentMethod,
    plan?.card_type,
    paymentInformation.card_type
  );

  if (!paymentMethodValue) {
    return null;
  }

  if (typeof paymentMethodValue === "string") {
    return {
      label: paymentMethodValue,
      cardType: firstValue(
        plan?.card_type,
        paymentInformation.card_type,
        paymentMethodValue
      ),
      lastFour: firstValue(
        plan?.last_four_digits,
        paymentInformation.last_four_digits
      ),
      expiry: firstValue(plan?.expiry_date, paymentInformation.expiry_date),
      cardholderName: firstValue(
        plan?.cardholder_name,
        plan?.cardholderName,
        paymentInformation.cardholder_name,
        paymentInformation.cardholderName,
        paymentInformation.card_holder_name
      ),
    };
  }

  const cardType = firstValue(
    paymentMethodValue.card_type,
    paymentMethodValue.cardType,
    paymentMethodValue.type,
    paymentMethodValue.brand,
    paymentMethodValue.method,
    plan?.card_type,
    paymentInformation.card_type,
    "Card"
  );

  return {
    label: cardType,
    cardType,
    lastFour: firstValue(
      paymentMethodValue.last_four_digits,
      paymentMethodValue.lastFour,
      paymentMethodValue.last4,
      plan?.last_four_digits
    ),
    expiry: firstValue(
      paymentMethodValue.expiry_date,
      paymentMethodValue.expiry,
      plan?.expiry_date
    ),
    cardholderName: firstValue(
      paymentMethodValue.cardholder_name,
      paymentMethodValue.cardholderName,
      paymentMethodValue.name,
      billingDetails.name,
      billingDetails.customer_name,
      plan?.cardholder_name,
      plan?.cardholderName,
      paymentInformation.cardholder_name,
      paymentInformation.cardholderName,
      paymentInformation.card_holder_name
    ),
  };
}

export function getSubscriptionStatusLabel(status) {
  return STATUS_LABELS[status] || "Subscription";
}

export function normalizeSubscriptionForStatusCard(plan) {
  if (!plan) {
    return null;
  }

  const rawSubscription = getRawSubscription(plan);
  const rawItems = getRawItems(plan);
  const firstItem = rawItems[0] || {};
  const price = firstItem.price || {};
  const product = firstItem.product || {};
  const priceId = firstValue(
    plan?.payment_information?.price_id,
    plan?.price_id,
    plan?.paddle_plan_id,
    price.id
  );
  const currency = getCurrency(plan, firstItem);
  const billingCycle = getBillingCycle(plan, firstItem);
  const unitAmount = getBillingPriceAmount(plan, firstItem);
  const status = plan.status === "deleted" ? "canceled" : plan.status || "canceled";
  const currentPeriod = firstValue(
    plan?.payment_information?.current_billing_period,
    rawSubscription.current_billing_period
  ) || {};
  const scheduledChange = firstValue(
    plan?.payment_information?.scheduled_change,
    rawSubscription.scheduled_change,
    plan?.scheduled_change
  );
  const nextBillingAt = firstValue(
    plan?.payment_information?.next_billed_at,
    firstItem.next_billed_at,
    plan?.next_billed_at,
    plan?.payment_information?.next_payment?.date
  );
  const collectionMode = firstValue(
    rawSubscription.collection_mode,
    plan?.payment_information?.collection_mode,
    "automatic"
  );
  const planName = firstValue(
    plan.plan_name,
    price.name,
    product.name,
    getPlanName(priceId),
    "Subscription"
  );
  const quantity = Number(firstItem.quantity) || 1;
  const discount = normalizeDiscount(rawSubscription.discount, currency);
  const subtotalAmount = unitAmount === null ? null : unitAmount * quantity;
  const totalAmount =
    subtotalAmount === null
      ? null
      : Math.max(subtotalAmount - (discount?.numericAmount || 0), 0);

  return {
    plan,
    provider: plan.is_billing === true ? "billing" : "classic",
    status,
    statusLabel: getSubscriptionStatusLabel(status),
    title: planName,
    planName,
    priceId,
    currency,
    unitAmount,
    priceLabel: formatSubscriptionMoney(unitAmount, currency),
    subtotalLabel: formatSubscriptionMoney(subtotalAmount, currency),
    totalLabel: formatSubscriptionMoney(totalAmount, currency),
    billingCycle,
    billingIntervalLabel: getBillingIntervalLabel(billingCycle),
    items: rawItems.map((item) => ({
      id: item?.price?.id || item?.product?.id || "",
      name: item?.product?.name || item?.price?.name || "Subscription item",
      quantity: item?.quantity || 1,
      recurring: item?.recurring !== false,
    })),
    currentPeriod: {
      startsAt: currentPeriod.starts_at || currentPeriod.startsAt || "",
      endsAt: currentPeriod.ends_at || currentPeriod.endsAt || "",
    },
    nextBillingAt,
    nextBillingLabel: formatSubscriptionDate(nextBillingAt),
    scheduledChange: scheduledChange
      ? {
          action: scheduledChange.action || "",
          effectiveAt: scheduledChange.effective_at || scheduledChange.effectiveAt || "",
          effectiveLabel: formatSubscriptionDate(
            scheduledChange.effective_at || scheduledChange.effectiveAt
          ),
        }
      : null,
    discount,
    collectionMode,
    paymentMethod: getPaymentMethod(plan),
    updatePaymentMethodUrl: getManagementUrl(plan, "update"),
    cancelUrl: getManagementUrl(plan, "cancel"),
    manageUrl: getManagementUrl(plan, "manage"),
    canUpdatePaymentMethod:
      collectionMode === "automatic" && !["paused", "canceled"].includes(status),
    canChangePlan:
      !["paused", "canceled"].includes(status),
    canManage:
      Boolean(getManagementUrl(plan, "manage")) && status !== "canceled",
  };
}

export function getScheduledChangeLabel(subscription) {
  const change = subscription?.scheduledChange;
  if (!change?.action || !change.effectiveLabel) {
    return "";
  }

  const actionLabels = {
    pause: "pause",
    cancel: "cancel",
    resume: "resume",
  };

  const action = actionLabels[change.action];
  return action
    ? `Your subscription is scheduled to ${action} on ${change.effectiveLabel}.`
    : "";
}

export function getRenewalLabel(subscription) {
  if (!subscription?.nextBillingLabel || subscription.status === "canceled") {
    return "";
  }

  if (subscription.scheduledChange?.action === "cancel") {
    return `Ends on ${subscription.scheduledChange.effectiveLabel || subscription.nextBillingLabel}`;
  }

  if (subscription.collectionMode === "automatic") {
    return "Auto-renews";
  }

  return `Next billing ${subscription.nextBillingLabel}`;
}

export function getAnnualizedMonthlyAmount(subscription) {
  const interval = subscription?.billingCycle?.interval;
  const frequency = subscription?.billingCycle?.frequency || 1;

  if (interval === "year" && subscription.unitAmount !== null) {
    return subscription.unitAmount / (MONTHS_PER_YEAR * frequency);
  }

  return subscription?.unitAmount ?? null;
}
